// frontend/pages/Signup.js — the same page as login.js wearing the same
// clothes: white card on light grey, blue accents, inline styles, no Tailwind.
//
// It used to be a dark slate Tailwind page with a blue→cyan gradient heading
// while login was a white card. Two pages, two visual languages, one step
// apart in the same flow.
//
// Only the presentation changed. Every field, the conditional blocks, the
// payload shape sent to /auth/register and the Meta Pixel event are the same
// as before.
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, Loader2, User, Mail, Lock, Phone, MapPin,
} from 'lucide-react';
import API_URL from '../api/baseURL';
import { ReactComponent as Logo } from '../assets/logo.svg';

const BLUE = '#3b82f6';
const BLUE_DARK = '#2563eb';
const TEXT = '#334155';
const MUTED = '#64748b';
const BORDER = '#e2e8f0';

const SPECIALIZATION_OPTIONS = [
  'General Mechanics',
  'Painting',
  'Engine',
  'Transmission',
  'Tuning',
  'Key Programming',
  'Electrical',
  'Diagnostics',
];

const PRO_ROLES = ['Car Workshop', 'Spare Parts Supplier'];

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: TEXT, marginBottom: 6 };
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '12px 14px',
  fontSize: 16, // 16px avoids iOS auto-zoom on focus
  color: TEXT, background: '#fff',
  border: `1px solid ${BORDER}`, borderRadius: 8,
  outline: 'none', transition: 'border-color .15s, box-shadow .15s',
};
const iconStyle = {
  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
  color: MUTED, pointerEvents: 'none',
};
const panelStyle = {
  background: '#f8fafc', border: `1px solid ${BORDER}`,
  borderRadius: 12, padding: 16, marginBottom: 18,
};

// One labelled text input with a left icon — the shape login.js uses for both
// of its fields, repeated here half a dozen times.
//
// Declared at module scope, NOT inside Signup. A component defined in a render
// body is a new type on every render, so React unmounts and remounts it and
// the field loses focus after each keystroke.
const TextField = ({
  label, icon: Icon, name, value, onChange,
  type = 'text', placeholder, required = true, autoComplete,
}) => (
  <div style={{ marginBottom: 18 }}>
    <label style={labelStyle}>{label}</label>
    <div style={{ position: 'relative' }}>
      <Icon size={16} style={iconStyle} />
      <input
        name={name} type={type}
        autoCapitalize="off" autoCorrect="off" spellCheck={false}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value} onChange={onChange} required={required}
        className="lg-input"
        style={{ ...inputStyle, paddingLeft: 38 }}
      />
    </div>
  </div>
);

const Signup = () => {
  const [form, setForm] = useState({
    accountType: 'Individual',
    name: '',
    email: '',
    password: '',
    role: '',
    phone: '',
    location: '',
    businessName: '',
    taxId: '',
    companyType: '',
    tvaApplicable: 'No',
    specializations: [],
    brands: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Plain type=password, switched rather than masked. Masking a type=text
  // field with -webkit-text-security stops GBoard typing into it on Android —
  // the same reason login.js does it this way.
  const passwordType = showPassword ? 'text' : 'password';

  const isPro = PRO_ROLES.includes(form.role);
  const isCompany = form.accountType === 'Company';
  const needsBusinessName = isCompany || isPro;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'role') {
      setForm({
        ...form,
        role: value,
        accountType: PRO_ROLES.includes(value) ? 'Company' : 'Individual',
      });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const toggleSpecialization = (opt) => {
    setForm((f) => ({
      ...f,
      specializations: f.specializations.includes(opt)
        ? f.specializations.filter((s) => s !== opt)
        : [...f.specializations, opt],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!acceptTerms) {
      setError("Vous devez accepter les Conditions d'Utilisation et la Politique de Confidentialité.");
      return;
    }

    let brandsArray = [];
    if (form.role === 'Car Workshop' && form.brands) {
      brandsArray = form.brands.split(',').map((b) => b.trim()).filter(Boolean);
    }

    const payload = {
      ...form,
      ...(needsBusinessName && { businessName: form.businessName }),
      ...(isCompany && {
        taxId: form.taxId,
        companyType: form.companyType,
        tvaApplicable: form.tvaApplicable,
      }),
      details: {
        phone: form.phone,
        location: form.location,
        ...(needsBusinessName
          ? {
              businessName: form.businessName,
              taxId: form.taxId,
              companyType: isCompany ? form.companyType : undefined,
              tvaApplicable: isCompany ? form.tvaApplicable : undefined,
            }
          : {}),
        ...(form.role === 'Car Workshop' && {
          specializations: form.specializations,
          brands: brandsArray,
        }),
      },
    };

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/register`, payload);
      // Meta Pixel: fire only on a CONFIRMED successful registration
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration', { content_name: form.role || 'account' });
      }
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || "L'inscription a échoué");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, { email: form.email });
      alert('Un nouvel e-mail de confirmation a été envoyé.');
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'envoi.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div dir="ltr" style={{
      minHeight: '100vh', width: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f1f5f9', padding: '24px 16px',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      direction: 'ltr', textAlign: 'left',
    }}>
      <style>{`
        .lg-input:focus { border-color:${BLUE} !important; box-shadow:0 0 0 3px rgba(59,130,246,.15) !important; }
        .lg-btn:hover:not(:disabled) { background:${BLUE_DARK} !important; }
        .lg-link:hover { text-decoration:underline; }
        .lg-ghost:hover { background:#f8fafc !important; }
        input::-ms-reveal, input::-ms-clear { display:none !important; }
        input::-webkit-credentials-auto-fill-button,
        input::-webkit-strong-password-auto-fill-button,
        input::-webkit-contacts-auto-fill-button,
        input::-webkit-caps-lock-indicator {
          visibility:hidden !important; display:none !important;
          pointer-events:none !important; width:0 !important; margin:0 !important;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{
        width: '100%', maxWidth: 420,
        background: '#fff', border: `1px solid ${BORDER}`,
        borderRadius: 16, boxShadow: '0 10px 40px rgba(15,23,42,.08)',
        padding: '36px 28px 28px',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Logo style={{ height: 30 }} />
            <span style={{ fontSize: 24, fontWeight: 800, color: BLUE, letterSpacing: '.5px' }}>1PARC</span>
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: TEXT, margin: 0 }}>
            {success ? 'Presque fini !' : 'Créer votre compte'}
          </h1>
          <p style={{ fontSize: 14, color: MUTED, marginTop: 6 }}>
            {success ? 'Il reste une étape' : 'Rejoignez l’écosystème 1PARC'}
          </p>
        </div>

        {/* Error */}
        {error && !success && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
            borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 16,
          }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        {!success ? (
          <form onSubmit={handleSubmit}>
            {/* Role */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Rôle</label>
              <select
                name="role" value={form.role} onChange={handleChange} required
                className="lg-input" style={inputStyle}
              >
                <option value="">Sélectionnez un rôle</option>
                <option value="Car Owner">Propriétaire de véhicule</option>
                <option value="Car Workshop">Atelier / Garage</option>
              </select>
            </div>

            {/* Account type — not asked of professionals, who are always a company */}
            {!isPro && (
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Type de compte</label>
                <select
                  name="accountType" value={form.accountType} onChange={handleChange} required
                  className="lg-input" style={inputStyle}
                >
                  <option value="Individual">Particulier</option>
                  <option value="Company">Entreprise</option>
                </select>
              </div>
            )}

            <TextField
              label="Nom complet" icon={User} name="name"
              value={form.name} onChange={handleChange}
              placeholder="Votre nom complet" autoComplete="name"
            />
            <TextField
              label="E-mail" icon={Mail} name="email" type="email"
              value={form.email} onChange={handleChange}
              placeholder="vous@exemple.com" autoComplete="email"
            />

            {/* Password — left icon, no in-field toggle, show/hide below. Same
                structure as login.js, for the same Android keyboard reason. */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Mot de passe</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={iconStyle} />
                <input
                  name="password" type={passwordType}
                  autoCapitalize="off" autoCorrect="off" spellCheck={false}
                  autoComplete="new-password"
                  placeholder="Choisissez un mot de passe"
                  value={form.password} onChange={handleChange} required
                  className="lg-input"
                  style={{ ...inputStyle, paddingLeft: 38 }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: MUTED, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: BLUE, cursor: 'pointer' }}
                />
                Afficher le mot de passe
              </label>
            </div>

            <TextField
              label="Téléphone" icon={Phone} name="phone" type="tel"
              value={form.phone} onChange={handleChange}
              placeholder="+225 00 00 00 00" autoComplete="tel"
            />
            <TextField
              label="Localisation" icon={MapPin} name="location"
              value={form.location} onChange={handleChange}
              placeholder="Ville, quartier" autoComplete="address-level2"
            />

            {/* Business details — companies and professionals */}
            {needsBusinessName && (
              <div style={panelStyle}>
                <div style={{ marginBottom: isCompany ? 16 : 0 }}>
                  <label style={labelStyle}>
                    {isCompany ? "Nom de l'entreprise" : "Nom de l'atelier / magasin"}
                  </label>
                  <input
                    type="text" name="businessName"
                    placeholder={isCompany ? "Raison sociale" : "Enseigne"}
                    value={form.businessName} onChange={handleChange} required
                    className="lg-input" style={inputStyle}
                  />
                </div>

                {isCompany && (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Numéro fiscal (NIF/STAT)</label>
                      <input
                        type="text" name="taxId" placeholder="Facultatif"
                        value={form.taxId} onChange={handleChange}
                        className="lg-input" style={inputStyle}
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Type d'entreprise</label>
                      <input
                        type="text" name="companyType" placeholder="SARL, SA, SAS…"
                        value={form.companyType} onChange={handleChange}
                        className="lg-input" style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>TVA</label>
                      <select
                        name="tvaApplicable" value={form.tvaApplicable} onChange={handleChange}
                        className="lg-input" style={inputStyle}
                      >
                        <option value="No">TVA non applicable</option>
                        <option value="Yes">TVA applicable</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Specializations — professionals only */}
            {isPro && (
              <div style={panelStyle}>
                <label style={labelStyle}>Spécialisations / Services</label>
                {/* Checkboxes rather than a multiple <select>: the old control
                    needed Ctrl or Cmd held down to pick more than one, which is
                    not a gesture a phone has. Same field, same payload. */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', marginBottom: 18 }}>
                  {SPECIALIZATION_OPTIONS.map((opt) => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: TEXT, cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={form.specializations.includes(opt)}
                        onChange={() => toggleSpecialization(opt)}
                        style={{ width: 15, height: 15, accentColor: BLUE, cursor: 'pointer', flexShrink: 0 }}
                      />
                      {opt}
                    </label>
                  ))}
                </div>

                <label style={labelStyle}>Marques gérées</label>
                <input
                  type="text" name="brands"
                  placeholder="ex : Mercedes, BMW, Toyota"
                  value={form.brands} onChange={handleChange}
                  className="lg-input" style={inputStyle}
                />
                <p style={{ fontSize: 12, color: MUTED, marginTop: 6, marginBottom: 0 }}>
                  Séparez les marques par des virgules.
                </p>
              </div>
            )}

            {/* Terms */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 22, fontSize: 13, color: MUTED, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                style={{ width: 15, height: 15, accentColor: BLUE, cursor: 'pointer', marginTop: 2, flexShrink: 0 }}
              />
              <span>
                J'accepte les{' '}
                <a href="/conditions" target="_blank" rel="noopener noreferrer" className="lg-link" style={{ color: BLUE, textDecoration: 'none', fontWeight: 500 }}>
                  Conditions d'Utilisation
                </a>{' '}
                et la{' '}
                <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="lg-link" style={{ color: BLUE, textDecoration: 'none', fontWeight: 500 }}>
                  Politique de Confidentialité
                </a>.
              </span>
            </label>

            {/* Submit */}
            <button
              type="submit" disabled={loading}
              className="lg-btn"
              style={{
                width: '100%', padding: '13px', background: BLUE, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background .15s',
              }}
            >
              {loading && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
              {loading ? 'Création…' : 'Créer mon compte'}
            </button>

            <p style={{ textAlign: 'center', fontSize: 14, color: MUTED, marginTop: 22 }}>
              Déjà inscrit ?{' '}
              <span onClick={() => navigate('/login')} className="lg-link" style={{ color: BLUE, cursor: 'pointer', fontWeight: 600 }}>
                Se connecter
              </span>
            </p>
          </form>
        ) : (
          /* Confirmation */
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#ecfdf5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 18px',
            }}>
              <CheckCircle2 size={32} color="#059669" />
            </div>
            <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, margin: '0 0 24px' }}>
              Un e-mail de confirmation a été envoyé à{' '}
              <b style={{ color: TEXT }}>{form.email}</b>. Cliquez sur le lien qu'il
              contient pour activer votre compte avant de vous connecter.
            </p>

            <button
              type="button" onClick={handleResend} disabled={resending}
              className="lg-ghost"
              style={{
                width: '100%', marginBottom: 10, padding: '12px',
                background: '#fff', border: `1px solid ${BORDER}`, color: TEXT,
                borderRadius: 8, fontSize: 14, fontWeight: 600,
                cursor: resending ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background .15s',
              }}
            >
              <Mail size={15} />
              {resending ? 'Envoi…' : "Renvoyer l'e-mail"}
            </button>

            <button
              type="button" onClick={() => navigate('/login')}
              className="lg-btn"
              style={{
                width: '100%', padding: '13px', background: BLUE, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
                cursor: 'pointer', transition: 'background .15s',
              }}
            >
              Retour à la connexion
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup;
